import {
  signIn,
  fetchAuthSession,
  SignInOutput,
  AuthSession,
  signOut,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { GenericCredentials, AuthProvider, AuthProviderForm, Session } from '../sessions.service';
import { SessionClaims } from '../../../config/sessions.config';
import * as jose from 'jose';
import { Amplify } from 'aws-amplify';

export interface AmplifyAuthGrantType {
  prepareAuthenticationPayload(credentials: GenericCredentials, config: AmplifyAuthProviderConfig): Promise<BodyInit>;
  getSignInFields(): AuthProviderForm;
}

export interface AmplifyAuthProviderConfig {
  grantType: AmplifyAuthGrantType;
  userPoolId: string;
  userPoolWebClientId: string;
  adminGroup?: string;
  ituGroup?: string;
  countryGroup?: string;
  label?: string;
  icon?: string;
  extractSessionIdFromToken: (access_token: jose.JWTPayload, id_token?: jose.JWTPayload) => Promise<string>;
  redirectTo: () => string;
}

export default class AmplifyCognitoAuthProvider implements AuthProvider {
  public label: string;
  public icon: string;
  public redirectTo: (role: any) => string;
  public supportsPasswordReset = true;

  constructor(
    public name: string,
    private config: AmplifyAuthProviderConfig
  ) {
    this.label = config.label ?? name;
    this.icon = config.icon ?? 'fa-key';
    this.redirectTo = config.redirectTo;
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.userPoolId,
          userPoolClientId: config.userPoolWebClientId,
          loginWith: {
            username: true,
          },
        },
      },
    });
  }

  async startSession(credentials: GenericCredentials): Promise<Session> {
    const username = credentials['username'];
    const password = credentials['password'];

    let signInResult: SignInOutput;
    try {
      signInResult = await signIn({
        username,
        password,
        options: {
          authFlowType: 'USER_PASSWORD_AUTH',
        },
      });
    } catch (error: any) {
      if (error.toString().includes('There is already a signed in user')) {
        await signOut({ global: true });
        signInResult = await signIn({
          username,
          password,
          options: {
            authFlowType: 'USER_PASSWORD_AUTH',
          },
        });
      } else {
        throw error;
      }
    }

    if (!signInResult.isSignedIn) {
      const challenge = signInResult.nextStep.signInStep;

      if (challenge === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        throw {
          challenge: 'NEW_PASSWORD_REQUIRED',
          info: signInResult.nextStep,
          username: username,
          message: 'You must set a new password before you can sign in.',
        };
      } else if (challenge === 'RESET_PASSWORD') {
        throw {
          challenge: 'RESET_PASSWORD',
          info: signInResult.nextStep,
          username: username,
          message: 'Your password needs to be reset.',
        };
      } else {
        throw {
          challenge: signInResult.nextStep.signInStep,
          info: signInResult.nextStep,
          username: username,
        };
      }
    }

    const authSession = await fetchAuthSession();
    const session = await this.createSessionFromAuthSession(username, authSession);

    return session;
  }

  private async createSessionFromAuthSession(username: string, authSession: AuthSession): Promise<Session> {
    const tokens = authSession.tokens!;
    const accessPayload = tokens.accessToken?.payload ?? {};
    const idPayload = tokens.idToken?.payload ?? {};
    const idTokenValue = tokens.idToken ? String(tokens.idToken) : '';
    const accessTokenValue = tokens.accessToken ? String(tokens.accessToken) : '';
    const bearerToken = idTokenValue || accessTokenValue;

    const sessionId = await this.config.extractSessionIdFromToken(accessPayload, idPayload);

    const groups = (idPayload['cognito:groups'] || accessPayload['cognito:groups'] || []) as string[];

    const sessionClaims: SessionClaims = {
      id: idPayload!.sub!,
      username: username,
      email: idPayload!['email'],
      name: idPayload!['email'],
      imageSrc: '',
      groups: groups,
      isAdmin: groups.includes('Admin'),
    };
    const expiresAt = tokens.accessToken ? tokens.accessToken.payload.exp! * 1000 : Date.now();

    return new Session(
      sessionId,
      this.name,
      { username },
      {},
      {},
      {
        httpHeaders: {
          ...(idTokenValue ? { 'X-Id-Token': idTokenValue } : {}),
          ...(bearerToken ? { Authorization: `Bearer ${bearerToken}` } : {}),
        },
      },
      Date.now(),
      sessionClaims,
      expiresAt,
      null,
      null,
      this.redirectTo
    );
  }

  async refreshSession(existingSession: Session): Promise<Session> {
    const refreshed = await fetchAuthSession({ forceRefresh: true });

    const session = await this.createSessionFromAuthSession(existingSession.sessionInfo.username, refreshed);
    return session;
  }

  public getSignInFields(): AuthProviderForm {
    return this.config.grantType.getSignInFields();
  }

  public async confirmNewPassword(newPassword: string): Promise<Session> {
    try {
      const result = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (!result.isSignedIn) {
        throw new Error('Failed to confirm new password');
      }

      const authSession = await fetchAuthSession();
      const username = String(authSession.tokens?.idToken?.payload['cognito:username'] || '');

      return await this.createSessionFromAuthSession(username, authSession);
    } catch (error) {
      throw error;
    }
  }

  public async resetPassword(username: string): Promise<void> {
    try {
      await resetPassword({ username });
    } catch (error) {
      throw error;
    }
  }

  public async confirmResetPassword(username: string, confirmationCode: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
    } catch (error) {
      throw error;
    }
  }
}
